/**
 * Data models for the Quick Review board.
 *
 * The board surfaces PA orders whose monitored fields drifted between the
 * value the order was worked in (medical_pa_order) and the newly ingested
 * value (quick_review_orders). Org scope comes from the auth context, so none
 * of these bodies carry it.
 * @module quickReviewModel
 */

/** Pagination window requested for the Quick Review board. */
export interface QuickReviewListRequest {
  /** Page number (0-indexed) */
  page: number;
  /** Results per page (1-100) */
  hits_per_page: number;
}

/**
 * One card on the Quick Review board.
 *
 * The monitored values here are the newly ingested ones, not the values the
 * order was worked in — the board says an order drifted, and `detail` says
 * how. The value fields mirror MONITORED_FIELDS, so a field added there
 * needs a matching field added here.
 */
export interface QuickReviewListItem {
  /** PA order id under review */
  order_id: string;
  /** Patient this order belongs to */
  patient_id: string;
  /** Id of the user the order is assigned to */
  assigned_to_id?: string | null;
  /** Display name of the assignee */
  assigned_to_name?: string | null;
  /** Newly ingested visit location */
  location?: string | null;
  /** Newly ingested date of service */
  date_of_service?: string | null;
  /** Newly ingested ordering practitioner */
  practitioner_name?: string | null;
  /** Newly ingested regimen or medication */
  regimen_name?: string | null;
  /** Newly ingested appointment instructions */
  instructions?: string | null;
  /** What put the order on the board */
  triggered_by?: string | null;
  /** Auth status of the order */
  auth_status?: string;
  /** Estimate value shown in the BO/Estimate column */
  estimate_value?: string;
  /** Patient first name */
  first_name?: string;
  /** Patient last name */
  last_name?: string;
  /** When the board row was last written (ISO 8601) */
  updated_at: string;
}

/** One page of pending Quick Review rows for one organization. */
export interface QuickReviewListResponse {
  /** Board cards on this page, newest first */
  items: QuickReviewListItem[];
  /** Pending rows on the whole board, not just this page */
  total_count: number;
  /** Page number returned (0-indexed) */
  page: number;
  /** Total number of pages at this page size */
  total_pages: number;
  /** Page size used for this response */
  hits_per_page: number;
}

/** Payload identifying the order to open from the board. */
export interface QuickReviewDetailRequest {
  /** PA order id to open */
  order_id: string;
}

/** Pending-row count response for the Quick Review board. */
export interface QuickReviewCountResponse {
  /** Number of pending rows on the whole board */
  total_count: number;
}

/** One monitored field, as the order was worked and as it was re-ingested. */
export interface QuickReviewFieldComparison {
  /** Monitored field name */
  field: string;
  /** Value the order was last worked in, from medical_pa_order */
  worked_value?: unknown;
  /** Newly ingested value, from quick_review_orders */
  new_value?: unknown;
  /** Whether the two values differ */
  changed: boolean;
}

/**
 * One order opened from the board, with every monitored field compared.
 *
 * `fields` always carries all of MONITORED_FIELDS whether they changed or
 * not, so the dashboard can show unchanged values as context. An order
 * edited outside Quick Review after it was boarded can legitimately come back
 * with every entry unchanged — that is a real state, not an error.
 */
export interface QuickReviewDetailResponse {
  /** PA order id under review */
  order_id: string;
  /** Patient this order belongs to */
  patient_id: string;
  /** Id of the user the order is assigned to */
  assigned_to_id?: string | null;
  /** Display name of the assignee */
  assigned_to_name?: string | null;
  /** What put the order on the board */
  triggered_by?: string | null;
  /** When the board row was last written (ISO 8601) */
  updated_at: string;
  /** Every monitored field, worked value against new */
  fields: QuickReviewFieldComparison[];
}

/**
 * Decisions the board accepts for a boarded order.
 *
 * Maps to the `decision` field on {@link QuickReviewDecisionRequest}.
 */
export enum QuickReviewDecision {
  /** Send the order back through the PA pipeline */
  REPROCESS = "reprocess",
  /** Accept the newly ingested values without reprocessing */
  NO_ACTION_NEEDED = "no_action_needed",
}

/** Payload applying a reviewer's decision to a boarded order. */
export interface QuickReviewDecisionRequest {
  /** PA order id being decided */
  order_id: string;
  /** Send the order back through the pipeline, or accept as-is */
  decision: QuickReviewDecision;
  /** Optional record of what the reviewer saw, stored on the board row */
  decision_payload?: Record<string, unknown> | null;
}

/** Board audit fields as they stand once a decision has been applied. */
export interface QuickReviewDecisionResult {
  /** Decided order id */
  order_id: string;
  /** False once decided */
  show_on_board: boolean;
  /** Review state the decision landed on */
  review_state: string;
  /** Auth subject of the reviewer */
  decided_by_id?: string | null;
  /** Reviewer email from the auth context */
  decided_by_name?: string | null;
  /** When the decision was applied (ISO 8601) */
  decided_at: string;
}
